import { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { Remove_User, Set_Cart_Modal_Open } from "../Redux/actions";
import {
  FaArrowLeft,
  FaShoppingCart,
  FaTimes,
  FaExclamationCircle,
} from "react-icons/fa";
import tw from "tailwind-styled-components";
import Cart from "../components/Cart";
import ProfileHeader from "../components/Profile/ProfileHeader";
import AccountMenu from "../components/Profile/AccountMenu";
import UserDetails from "../components/Profile/UserDetails";
import AddressSection from "../components/Profile/AddressSection";
import useProfile from "../hooks/useProfile";

const ProfilePage = () => {
  const location = useLocation();
  const user = useSelector((state) => state.UserReducer.user); // Get user from Redux store
  const isCartModalOpen = useSelector(
    (state) => state.CartReducer.isCartModalOpen,
  ); // Cart modal state from Redux
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { fetchProfile } = useProfile();
  const [isUserDetailsOpen, setIsUserDetailsOpen] = useState(false);
  const [isAddressModalOpen, setIsAddressModalOpen] = useState(false);
  const [openOnAddAddress, setOpenOnAddAddress] = useState(false);

  // Initialize form data and fetch profile
  useEffect(() => {
    if (!user) {
      navigate("/"); // redirect to home if no user is logged
    }
  }, [user, navigate, fetchProfile]);

  useEffect(() => {
    if (location.state?.openAddressModal) {
      setIsAddressModalOpen(true);
      setOpenOnAddAddress(true);
      window.history.replaceState({}, document.title); // Clear the state so the modal doesn't reopen on browser refresh
    }
  }, [location.state]);

  // Prevent body scroll when Address Modal is open
  useEffect(() => {
    if (isAddressModalOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }
    return () => {
      document.body.style.overflow = "unset";
    };
  }, [isAddressModalOpen]);

  const handleLogout = () => {
    // Clear user data and navigate to home on logout
    dispatch(Remove_User());
    navigate("/");
  };

  const handleBack = () => {
    // Navigate back to previous page
    navigate(-1);
  };

  const handleOpenCartModal = () => {
    if (user) {
      dispatch(Set_Cart_Modal_Open(true));
    }
  };

  const handleCloseCartModal = () => {
    dispatch(Set_Cart_Modal_Open(false));
  };

  const handleHelpCenter = () => {
    navigate("/help");
  };

  const handleChangeLanguage = () => {};

  const handleUserClick = () => {
    setIsUserDetailsOpen(true);
  };

  const handleCloseUserDetails = () => {
    setIsUserDetailsOpen(false);
  };

  const ProfileNavbar = () => (
    <TopNavbar>
      <NavLeft>
        <BackButton onClick={handleBack}>
          <FaArrowLeft className="w-5 h-5" />
        </BackButton>
        <PageTitle>My Account</PageTitle>
      </NavLeft>
      {user && (
        <NavRight>
          <NavIconButton onClick={handleOpenCartModal}>
            <FaShoppingCart className="w-5 h-5" />
          </NavIconButton>
        </NavRight>
      )}
    </TopNavbar>
  );

  if (!user) {
    return (
      <PageContainer>
        <ProfileNavbar />
        <div className="flex-1 flex flex-col items-center justify-center py-20">
          <FaExclamationCircle className="text-6xl text-gray-500 mb-4" />
          <h2 className="text-xl font-semibold text-gray-500 mb-2">
            Please Login or Register
          </h2>
          <p className="text-gray-500">You need to be logged in to access your profile.</p>
        </div>
      </PageContainer>
    );
  }

  // Use the most up-to-date data available
  const displayData = user;

  return (
    <PageContainer>
      <ProfileNavbar />
      <ProfileHeader // Profile Header Component
        displayData={displayData}
        onUserClick={handleUserClick}
        onHelpCenter={handleHelpCenter}
        onChangeLanguage={handleChangeLanguage}
      />

      {/* Account Menu Component */}
      <AccountMenu onLogout={handleLogout} displayData={displayData} />

      {/* Cart Modal */}
      <Cart isOpen={isCartModalOpen} onClose={handleCloseCartModal} />

      {/* User Details Modal */}
      <UserDetails
        isOpen={isUserDetailsOpen}
        onClose={handleCloseUserDetails}
        displayData={displayData}
        openOnAddAddress={location.state?.openAddressModal}
        onOpenAddressModal={() => setIsAddressModalOpen(true)}
      />
      {isAddressModalOpen && (
        <ModalOverlay>
          <ModalContainer>
            <ModalHeader>
              <ModalTitle>Manage Addresses</ModalTitle>
              <CloseButton onClick={() => setIsAddressModalOpen(false)}>
                <FaTimes />
              </CloseButton>
            </ModalHeader>
            <ModalContent>
              <AddressSection openOnAddAddress={openOnAddAddress} />
            </ModalContent>
          </ModalContainer>
        </ModalOverlay>
      )}
    </PageContainer>
  );
};

// Styled Components
const ModalOverlay = tw.div`
  fixed inset-0 bg-black bg-opacity-50 flex justify-end z-[101] transition-all duration-500
`;

const ModalContainer = tw.div`
  bg-white w-full sm:w-96 lg:w-1/3 h-full shadow-2xl flex flex-col rounded-l-lg transition-all duration-500 transform translate-x-0
`;

const ModalHeader = tw.div`
  p-4 flex justify-between items-center bg-white border-b border-gray-100 rounded-tl-lg
`;

const ModalTitle = tw.h2`
  text-lg font-semibold text-gray-800
`;

const CloseButton = tw.button`
  text-gray-400 hover:text-gray-600 transition-colors p-2
`;

const ModalContent = tw.div`
  flex-1 overflow-y-auto p-4
`;

const PageContainer = tw.div`
  min-h-screen w-full bg-white
`;

const TopNavbar = tw.div`
  flex items-center justify-between p-3 md:p-2 bg-white border-b border-gray-200 sticky top-0 z-10
`;

const NavLeft = tw.div`
  flex items-center space-x-3 md:space-x-4
`;

const NavRight = tw.div`
  flex items-center space-x-4 md:space-x-6
`;

const BackButton = tw.button`
  text-gray-600 hover:text-gray-900 p-2 rounded-lg hover:bg-gray-100 transition-colors
`;

const PageTitle = tw.h1`
  text-lg font-semibold text-gray-900 tracking-wider md:text-xl
`;

const NavIconButton = tw.button`
  p-1 text-gray-600 hover:text-gray-800 transition-colors md:p-2
`;

export default ProfilePage;
